import { get } from 'lit-translate';
import type { PopoverListItem } from '@o2d/popover-list';
import { CommandName } from '../commands/command-name.enum';
import { hasPrivilege } from '../helpers/privilege.helper';
import { actionRegistry } from '../actions/action-registry';
import { ActionIds } from '../actions/action.types';
import { NavigationHelper } from '../helpers/navigation.helper';
import { isTenantThemeEnabled } from '../tenant-feature-flags';
// Side-effect import registers the `<ha-papierkorb-dialog>` custom element (runs its @customElement
// decorator). Without it the named/type-only import is elided by the production bundler (esbuild
// type-elision), the element stays unregistered, and document.createElement returns an
// HTMLUnknownElement whose .open() is undefined → "TypeError: x.open is not a function".
import '../components/ha-papierkorb-dialog/ha-papierkorb-dialog.component';
import type { HaPapierkorbDialog } from '../components/ha-papierkorb-dialog/ha-papierkorb-dialog.component';
// Side-effect import for the same reason: registers `<ha-release-notes-dialog>`, which
// DialogHelper.showDialog looks up via customElements.get().
import '../components/ha-release-notes-dialog/ha-release-notes-dialog.component';
import { DialogHelper } from '../helpers/dialog.helper';
import { APP_VERSION } from '../release-notes.data';
import { DialogCommandName } from '../components/ha-dialog/dialog-command.enum';
import type { DialogCommand } from '../components/ha-dialog/dialog-command.interface';

/**
 * Opens the Papierkorb (trash) dialog on demand (O2D-PRD-101): creates the element, appends it to
 * the document body, wires close-cleanup (the dialog's X dispatches `dialog.message` CLOSE), and
 * shows it. Mirrors the UiHelper.confirm open/close pattern.
 */
function openPapierkorbDialog(): void {
    const dialog = document.createElement('ha-papierkorb-dialog') as HaPapierkorbDialog;

    const listener = {
        handleEvent: (event: CustomEvent<{ value: DialogCommand<unknown> }>) => {
            if (event.detail.value.command === DialogCommandName.CLOSE) {
                dialog.removeEventListener('dialog.message', listener);
                dialog.remove();
            }
        },
    };
    dialog.addEventListener('dialog.message', listener);

    document.body.append(dialog);
    dialog.open();
}

/**
 * Generates submenu items for the "Einstellungen" (settings) submenu with
 * synchronous authorization.
 *
 * Menu items are disabled when the user lacks the required privilege.
 * Privilege checks are synchronous via hasPrivilege() from privilege.helper.ts.
 *
 * Used by `ha-app.component.ts`'s sidebar settings link (operator request
 * 2026-08-17 — this used to be a `TimelineToolbarItem` rendered as a burger
 * menu in every board's `.timeline-toolbar`; it moved to a plain link at the
 * top of `ha-app`'s persistent sidebar, above the mode switch, for ALL board
 * views at once, so the submenu actions below — translations, tenant settings
 * ("Allgemein"), theme (only with the tenant switch on), Papierkorb, release
 * notes, logout — stay defined in
 * exactly one place).
 *
 * The release-notes entry carries the app version in its label and needs no
 * privilege: every logged-in user may read what changed.
 *
 * The iCal export entry is removed because the calendar feeds answer 410 Gone
 * (descriptions are tiptap documents, which the calendar export cannot read
 * yet). Bring it back only when the feeds work again.
 *
 * @param flags The tenant's runtime flags; a parameter (defaulting to the injected
 *              global) so tests under vitest's node environment can pass them in.
 * @returns Array of PopoverListItem with current authorization state.
 */
export function getSubMenuItems(flags: Window['__FEATURE_FLAGS__'] = globalThis.window?.__FEATURE_FLAGS__): PopoverListItem[] {
    const canEditTranslations = hasPrivilege(CommandName.SAVE_TRANSLATION_OVERRIDE);
    const canEditTheme = hasPrivilege(CommandName.SAVE_TENANT_THEME);
    const canEditTenantSettings = hasPrivilege(CommandName.SAVE_TENANT_SETTINGS);
    const canRestore = hasPrivilege(CommandName.RESTORE_ENTITY);
    const canLogout = hasPrivilege(CommandName.LOGOUT);

    // The tenant theme editor (feature plan 0054) exists only while the tenant's switch is on —
    // the route is filtered out otherwise, so the entry would lead nowhere.
    const themeItems: PopoverListItem[] = isTenantThemeEnabled(flags)
        ? [{
            id: 'theme',
            label: get('mainToolbar.theme'),
            type: 'item',
            payload: () => NavigationHelper.goto('/settings/theme'),
            disabled: !canEditTheme,
        }]
        : [];

    return [
        // Every logged-in user may change their own profile (self policy, no role right): always enabled.
        {
            id: 'own-profile',
            label: get('mainToolbar.ownProfile'),
            type: 'item',
            payload: () => NavigationHelper.goto('/settings/profile'),
            disabled: false,
        },
        {
            id: 'translations',
            label: get('mainToolbar.translations'),
            type: 'item',
            payload: () => NavigationHelper.goto('/settings/translations'),
            disabled: !canEditTranslations,
        },
        // Tenant-wide settings, today the start page (feature plan 0068). Always present, like the route.
        {
            id: 'tenant-settings',
            label: get('mainToolbar.tenantSettings'),
            type: 'item',
            payload: () => NavigationHelper.goto('/settings/general'),
            disabled: !canEditTenantSettings,
        },
        ...themeItems,
        {
            id: 'papierkorb',
            label: get('mainToolbar.papierkorb'),
            type: 'item',
            payload: () => openPapierkorbDialog(),
            disabled: !canRestore,
        },
        {
            id: 'release-notes',
            label: APP_VERSION
                ? `${get('mainToolbar.releaseNotes')} · ${APP_VERSION}`
                : get('mainToolbar.releaseNotes'),
            type: 'item',
            payload: () => DialogHelper.showDialog('ha-release-notes-dialog', {}),
            disabled: false,
        },
        {
            id: 'logout',
            label: get('general.logout'),
            type: 'item',
            payload: () => actionRegistry.execute(ActionIds.AUTH_LOGOUT),
            disabled: !canLogout,
        },
    ];
}
